import type { TourActionContext, TourDefinition } from './types';
import { getRouter } from '@/router/instance';
import { clickAnimated, clickFirstRowIn, delay } from '@/utils/tour/actions';
import { waitForCondition, waitForTourElement } from '@/utils/tour/waitForElement';

/**
 * Scripted playback demo for Review Projects: shared workspaces that gather
 * documents (the data room), playbook analyses, an AI assistant, a living
 * report, and the team around one review effort. The demo only navigates —
 * opening the seeded project and walking its panes and pages; it never
 * creates, sends, or edits anything, so there is no residue to clean up.
 */

/** Routes that mean "a project is open": the workspace and its sub pages. */
const PROJECT_ROUTES = new Set([
  'review-project-detail',
  'review-project-statistics',
  'review-project-tasks',
  'review-project-settings',
]);

// Whether the demo actually opened a project — set synchronously by the
// open action, so the detail scenes' prerequisites can skip instantly on
// orgs with no projects instead of burning their target timeouts one by
// one. The route check covers resumes that land directly on the detail.
let projectOpened = false;

async function openFirstProject(ctx: TourActionContext): Promise<void> {
  projectOpened = false;
  projectOpened = await clickFirstRowIn(ctx, 'rp.projects-table');
}

const projectOpen = (): boolean =>
  projectOpened || PROJECT_ROUTES.has(String(getRouter().currentRoute.value.name));

/** Back to the list — every project page lives on a :uid route. */
async function goBackToList(): Promise<void> {
  try {
    await getRouter().push({ name: 'review-projects' });
  } catch {
    // Navigation declined; the scene still steps back.
  }
  await delay(400);
}

/**
 * `offsetParent` alone is not enough here: the workspace's chat drawer stays
 * mounted when closed (it holds a live thread and an unsent draft) and is
 * merely animated to zero width, which still leaves it an offsetParent. A pane
 * with no box on screen is not open, so measure one.
 */
function isVisible(el: HTMLElement | null): boolean {
  if (el === null || el.offsetParent === null) return false;
  const box = el.getBoundingClientRect();
  return box.width > 0 && box.height > 0;
}

/**
 * Scenes self-locate: each ensures its own project page is the current route
 * before its target resolves, so Back/skip never strands a scene on the wrong
 * page. The overlay's own `route` navigation can't be used — these routes take
 * the project's `:uid`, which it does not carry.
 */
function ensureProjectPage(name: string): () => Promise<void> {
  return async () => {
    const uid = getRouter().currentRoute.value.params.uid;
    if (typeof uid !== 'string' || getRouter().currentRoute.value.name === name) {
      return;
    }
    try {
      await getRouter().push({ name, params: { uid } });
    } catch {
      return;
    }
    await delay(500);
  };
}

const ensureWorkspace = ensureProjectPage('review-project-detail');

/**
 * The report and the chat are opened from the title row rather than always
 * mounted, so their scenes open them first. The flag remembers the TOUR
 * opened one, so it is only closed again if the user did not have it open by
 * choice.
 */
function openFromTitleRow(
  toggleKey: string,
  paneKey: string,
  remember: (opened: boolean) => void
): (ctx: TourActionContext) => Promise<void> {
  return async ctx => {
    await ensureWorkspace();
    if (isVisible(document.querySelector<HTMLElement>(`[data-tour="${paneKey}"]`))) {
      return;
    }
    const toggle = await waitForTourElement(toggleKey, 3000);
    if (!toggle || ctx.isCancelled()) {
      return;
    }
    await clickAnimated(toggle, ctx);
    const pane = await waitForCondition(() => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${paneKey}"]`);
      return isVisible(el) ? el : null;
    }, 3000);
    if (!pane || ctx.isCancelled()) {
      return;
    }
    remember(true);
    ctx.setHighlight(pane);
    if (ctx.animate) {
      // Let the opened pane settle before the next bubble anchors to it.
      await delay(600);
    }
  };
}

function closeFromTitleRow(
  toggleKey: string,
  wasOpenedByTour: () => boolean,
  remember: (opened: boolean) => void
): (ctx: TourActionContext) => Promise<void> {
  return async ctx => {
    if (!wasOpenedByTour()) {
      return;
    }
    const toggle = document.querySelector<HTMLElement>(`[data-tour="${toggleKey}"]`);
    if (!toggle) {
      return;
    }
    remember(false);
    await clickAnimated(toggle, ctx);
  };
}

let chatOpenedByTour = false;
let reportOpenedByTour = false;

const openChat = openFromTitleRow('rp.chat-toggle', 'rp.chat', v => (chatOpenedByTour = v));
const closeChat = closeFromTitleRow(
  'rp.chat-toggle',
  () => chatOpenedByTour,
  v => (chatOpenedByTour = v)
);
const openReport = openFromTitleRow('rp.report-toggle', 'rp.report', v => (reportOpenedByTour = v));
const closeReport = closeFromTitleRow(
  'rp.report-toggle',
  () => reportOpenedByTour,
  v => (reportOpenedByTour = v)
);

export const reviewProjectsTour: TourDefinition = {
  key: 'review-projects',
  title: 'Review Projects',
  description: 'A shared workspace for documents, analyses, and findings.',
  entryRoute: 'review-projects',
  // Mirrors the sidebar's gating exactly: the org must have the feature AND
  // the user must be allowed to use it.
  requiredPermission: 'can_use_review_projects',
  onComplete: async () => {
    try {
      await getRouter().push({ name: 'review-projects' });
    } catch {
      // Navigation declined; completing the tour still stands.
    }
  },
  steps: [
    {
      id: 'projects',
      route: 'review-projects',
      target: 'rp.projects-table',
      placement: 'top',
      title: 'Your review projects',
      body:
        'Shared workspaces that organize contract review work: documents, ' +
        'analyses, and findings in one place. Let’s open one.',
      actions: [{ kind: 'custom', run: openFirstProject }],
    },
    {
      id: 'data-room',
      prerequisite: projectOpen,
      onEnter: ensureWorkspace,
      target: 'rp.data-room',
      placement: 'right',
      title: 'The data room',
      body:
        'Every document under review lives here, with tags, notes, and ' +
        'read coverage tracked per file. Collapse it when the analysis needs ' +
        'the room.',
      backActions: [{ kind: 'custom', run: goBackToList }],
    },
    {
      id: 'analysis',
      prerequisite: projectOpen,
      onEnter: ensureWorkspace,
      target: 'rp.analysis',
      placement: 'top',
      title: 'Playbook analyses',
      body:
        'Add an analysis from a playbook, then drag data-room files onto its ' +
        'tab — or select them and press Run. Findings land in this grid.',
    },
    {
      id: 'chat',
      prerequisite: projectOpen,
      onEnter: ensureWorkspace,
      target: 'rp.chat-toggle',
      placement: 'bottom',
      title: 'Ask the project',
      body:
        'The assistant answers from the project’s own documents. Chat your ' +
        'way to answers, questions, and even report drafts.',
      actions: [{ kind: 'custom', run: openChat }],
    },
    {
      id: 'chat-open',
      prerequisite: projectOpen,
      target: 'rp.chat',
      placement: 'left',
      title: 'It knows the data room',
      body:
        'Files you select in the data room become the chat’s focus, and ' +
        'anything you upload here joins the room.',
      actions: [{ kind: 'custom', run: closeChat }],
      backActions: [{ kind: 'custom', run: closeChat }],
    },
    {
      id: 'report',
      prerequisite: projectOpen,
      onEnter: ensureWorkspace,
      target: 'rp.report-toggle',
      placement: 'bottom',
      title: 'A living report',
      body:
        'One report per project: write it yourself, or let the report ' +
        'agent draft it from the data room.',
      actions: [{ kind: 'custom', run: openReport }],
    },
    {
      id: 'report-open',
      prerequisite: projectOpen,
      target: 'rp.report',
      placement: 'left',
      title: 'Beside the evidence',
      body:
        'The report shares the column with the analysis, so you can write ' +
        'from the findings — or expand it to fill the workspace.',
      actions: [{ kind: 'custom', run: closeReport }],
      backActions: [{ kind: 'custom', run: closeReport }],
    },
    {
      id: 'pages',
      prerequisite: projectOpen,
      onEnter: ensureWorkspace,
      target: 'rp.page-menu',
      placement: 'bottom-end',
      title: 'The rest of the project',
      body:
        'Statistics, background tasks and settings are pages of their own — ' +
        'this menu is how you get between them.',
    },
    {
      id: 'statistics',
      prerequisite: projectOpen,
      onEnter: ensureProjectPage('review-project-statistics'),
      target: 'rp.statistics',
      placement: 'top',
      title: 'Project statistics',
      body: 'Coverage, tags, questions, and findings in numbers: how far the ' + 'review has come.',
    },
    {
      id: 'tasks',
      prerequisite: projectOpen,
      onEnter: ensureProjectPage('review-project-tasks'),
      target: 'rp.tasks',
      placement: 'bottom',
      title: 'Background work',
      body:
        'Auto-tagging and report generation run as background tasks; ' +
        'follow their progress here.',
    },
    {
      id: 'members',
      prerequisite: projectOpen,
      onEnter: ensureProjectPage('review-project-settings'),
      target: 'rp.settings-members',
      placement: 'top',
      title: 'Bring your team',
      body:
        'Add teammates as members, and give outside reviewers access ' + 'through external links.',
    },
    {
      id: 'finale',
      title: 'That is Review Projects',
      body:
        'Documents, analyses, chat, and reporting in one shared space. ' +
        'Start your own with Create project.',
    },
  ],
};
